import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { cn } from '@/lib/cn';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

type PaginationProps = {
  pageIndex: number;
  hasNext: boolean;
  hasPrev: boolean;
  onNext: () => void;
  onPrev: () => void;
  loading?: boolean;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  className?: string;
};

export function Pagination({
  pageIndex,
  hasNext,
  hasPrev,
  onNext,
  onPrev,
  loading,
  pageSize,
  onPageSizeChange,
  className
}: PaginationProps) {
  const { t } = useTranslation();
  const displayPage = pageIndex + 1;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 border-t border-[var(--fv-border)] px-3 py-2',
        className
      )}
    >
      <p className="text-sm text-[var(--fv-text-muted)]">
        {loading ? t('common.loadingPage') : t('common.pageOf', { page: displayPage })}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {onPageSizeChange && pageSize != null && (
          <label className="flex items-center gap-2 text-sm text-[var(--fv-text-muted)]">
            {t('common.pageSize')}
            <Select
              className="min-w-[4.5rem]"
              value={String(pageSize)}
              onValueChange={(v) => onPageSizeChange(Number(v))}
              options={PAGE_SIZE_OPTIONS.map((n) => ({ value: String(n), label: String(n) }))}
              aria-label={t('common.pageSize')}
            />
          </label>
        )}
        <Button variant="secondary" size="sm" disabled={!hasPrev || loading} onClick={onPrev}>
          {t('common.prev')}
        </Button>
        <Button variant="secondary" size="sm" disabled={!hasNext || loading} onClick={onNext}>
          {t('common.next')}
        </Button>
      </div>
    </div>
  );
}
